import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AGE_OF_MAJORITY, EDUCATION_START_AGE, FACTION_DEMAND_TAX, formatDate } from '@pk/shared';
import type { AllianceDurationYears, FactionView, HouseView, Id, Letter, Proposal, SchemeView, WorldView } from '@pk/shared';
import { actorOf, memberIds, useCharacters, useCommand, useHouse, useWorld } from '../api/hooks';
import { Badge, Bar, Btn, Collapsible, Empty, ErrorBox, Field, Inline, Loading, PageHeader, Panel, Screen, ScreenTop, ScrollArea, Section, Segmented, Sheet } from '../components/ui';
import { IconCheck, IconDagger, IconLetter, IconQuill, IconScroll, IconShield, IconWar, IconX } from '../components/icons';
import { CharLink, CharacterPicker, HouseBadge, TitleLink, useEta } from '../components/game';
import '../styles/court.css';

const MAX_LEN = 600;
type Cmd = ReturnType<typeof useCommand>;
type Tab = 'inbox' | 'letters' | 'relations' | 'intrigue' | 'factions';
const TABS: Tab[] = ['inbox', 'letters', 'relations', 'intrigue', 'factions'];

export function CourtScreen() {
  const { t } = useTranslation();
  const house = useHouse();
  const world = useWorld();
  const [params, setParams] = useSearchParams();
  const [compose, setCompose] = useState<{ to: Id | null } | null>(null);
  const [alliance, setAlliance] = useState(false);
  const [wardship, setWardship] = useState(false);
  const [sortBy, setSortBy] = useState<'soon' | 'new'>('soon');
  const cmd = useCommand();

  if (house.isLoading || world.isLoading) return <Loading />;
  if (house.isError || world.isError || !house.data || !world.data) return <ErrorBox error={house.error ?? world.error} onRetry={() => { void house.refetch(); void world.refetch(); }} />;
  const h = house.data;
  const day = world.data.day;
  const mine = memberIds(h);
  const rawTab = params.get('tab');
  const tab: Tab = TABS.includes(rawTab as Tab) ? (rawTab as Tab) : 'inbox';
  const setTab = (v: Tab) => setParams(v === 'inbox' ? {} : { tab: v }, { replace: true });

  const received = h.letters.filter((l) => mine.has(l.toId)).sort((a, b) => b.sentDay - a.sentDay);
  const sentLetters = h.letters.filter((l) => mine.has(l.fromId) && !mine.has(l.toId));
  const allLetters = [...received, ...sentLetters].sort((a, b) => b.sentDay - a.sentDay);
  const unreadLetters = received.filter((l) => !l.read && l.arriveDay <= day);
  const byNewest = (a: Proposal, b: Proposal) => Number(b.status === 'pending') - Number(a.status === 'pending') || b.sentDay - a.sentDay;
  const incoming = [...h.incomingProposals].sort(byNewest);
  const outgoing = [...h.outgoingProposals].sort(byNewest);
  // Open proposals: the ones about to lapse first (default) or simply the newest.
  const incomingOpen = incoming.filter((p) => p.status === 'pending')
    .sort((a, b) => (sortBy === 'soon' ? a.expiresDay - b.expiresDay : b.sentDay - a.sentDay));
  const incomingDone = incoming.filter((p) => p.status !== 'pending');
  const awaiting = incomingOpen.filter((p) => p.arriveDay <= day).length;
  const inboxCount = awaiting + unreadLetters.length;
  const activeWars = h.wars.filter((w) => w.endedDay === null);
  const pendingOut = outgoing.filter((p) => p.status === 'pending').length;
  const plots = h.plotsAgainstUs.length;
  const intrigueCount = h.schemes.length + plots;

  const count = (n: number, solid = true) => (n > 0 ? <Badge solid={solid} tone={solid ? 'gold' : 'plain'}>{n}</Badge> : null);
  const label = (text: string, n: number, solid = true) => <span className="court-tab">{text}{count(n, solid)}</span>;

  return (
    <Screen className="court-page">
      <ScreenTop>
      <PageHeader icon={<IconScroll />} title={t('court.title')} subtitle={t('court.sub')}
        right={<Btn variant="primary" icon={<IconQuill />} onClick={() => setCompose({ to: null })}>{t('court.write')}</Btn>} />

      <Segmented<Tab> value={tab} onChange={setTab} options={[
        { value: 'inbox', label: label(t('court.tab.inbox'), inboxCount) },
        { value: 'letters', label: label(t('court.tab.letters'), 0) },
        { value: 'relations', label: label(t('court.tab.relations'), activeWars.length, false) },
        { value: 'intrigue', label: label(t('court.tab.intrigue'), plots, true) },
        { value: 'factions', label: label(t('court.tab.factions'), h.factions.length, false) },
      ]} />
      </ScreenTop>

      <ScrollArea>
      {tab === 'inbox' && (
        <>
          {incomingOpen.length === 0 && unreadLetters.length === 0 && <Panel><Empty>{t('court.nothingWaiting')}</Empty></Panel>}
          {incomingOpen.length > 0 && (
            <Section title={t('court.awaitingYou')} right={<>{incomingOpen.length > 1 && (
              <button type="button" className="sort-chip" onClick={() => setSortBy(sortBy === 'soon' ? 'new' : 'soon')} aria-label={t('courtx.sortLabel')}>
                {sortBy === 'soon' ? t('courtx.sortSoon') : t('courtx.sortNew')}
              </button>
            )}{count(awaiting)}</>}>
              <Panel><div className="attn-list">{incomingOpen.map((p) => <ProposalRow key={p.id} p={p} incoming day={day} house={h} cmd={cmd} />)}</div></Panel>
              <Inline error={cmd.error} />
            </Section>
          )}
          {unreadLetters.length > 0 && (
            <Section title={t('court.unreadLetters')} right={count(unreadLetters.length)}>
              <Panel>{unreadLetters.map((l) => <LetterCard key={l.id} l={l} inbox day={day} onReply={() => setCompose({ to: l.fromId })} />)}</Panel>
            </Section>
          )}
          {incomingDone.length > 0 && (
            <Panel className="more">
              <Collapsible title={t('court.answered')} summary={incomingDone.length}>
                <div className="attn-list">{incomingDone.map((p) => <ProposalRow key={p.id} p={p} incoming day={day} house={h} cmd={cmd} />)}</div>
              </Collapsible>
            </Panel>
          )}
        </>
      )}

      {tab === 'letters' && (
        <Section title={t('courtx.favoursTitle')} right={count(h.hooks.length, false)}>
          <Panel>
            <div className="court-ward">
              <p className="small muted">{t('courtx.wardshipHelp')}</p>
              <Btn variant="quiet" icon={<IconLetter />} onClick={() => setWardship(true)}>{t('court.offerWardship')}</Btn>
            </div>
            {h.hooks.length === 0 && <Empty>{t('courtx.noHooks')}</Empty>}
            <ul className="list">
              {h.hooks.map((k) => (
                <li key={k.id}>
                  <span className="grow">
                    <b>{k.direction === 'held' ? t('letters.hookHeld') : t('letters.hookOwed')}</b>
                    <span className="lr-sub">
                      {k.direction === 'held' ? <CharLink id={k.targetId} /> : <CharLink id={k.holderId} />}
                      {' · '}{k.spent !== null ? t('letters.hookSpent', { day: formatDate(k.spent) }) : t('letters.hookUnspent')}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        </Section>
      )}
      {tab === 'letters' && (
        allLetters.length === 0 ? <Panel><Empty>{t('court.noLetters')}</Empty></Panel> : (
          <Panel>
            {allLetters.map((l) => {
              const inbox = mine.has(l.toId);
              return <LetterCard key={l.id} l={l} inbox={inbox} day={day} onReply={() => setCompose({ to: l.fromId })} />;
            })}
          </Panel>
        )
      )}

      {tab === 'relations' && (
        <>
          <div className="court-rel-actions">
            <Btn variant="quiet" icon={<IconShield />} onClick={() => setAlliance(true)}>{t('letters.alliance.propose')}</Btn>
          </div>
          <Section title={t('court.alliances')} right={count(h.allyHouseIds.length, false)}>
            {h.allyHouseIds.length === 0 ? <Panel><Empty>{t('court.noAlliances')}</Empty></Panel> : (
              <Panel><ul className="list">{h.allyHouseIds.map((id) => <li key={id}><Link className="link" to={`/tree/${id}`}><HouseBadge houseId={id} /></Link></li>)}</ul></Panel>
            )}
          </Section>
          <Section title={t('court.pacts')} right={count(h.napHouseIds.length, false)}>
            {h.napHouseIds.length === 0 ? <Panel><Empty>{t('court.noPacts')}</Empty></Panel> : (
              <Panel><ul className="list">{h.napHouseIds.map((id) => (
                <li key={id}><Link className="link" to={`/tree/${id}`}><HouseBadge houseId={id} /></Link><span className="small muted">{t('court.pactLabel')}</span></li>
              ))}</ul></Panel>
            )}
          </Section>
          <Section title={t('court.wars')} right={count(activeWars.length, false)}>
            {activeWars.length === 0 ? <Panel><Empty>{t('court.noWars')}</Empty></Panel> : (
              <Panel><ul className="list">{activeWars.map((w) => (
                <li key={w.id}><span className="grow"><b>{w.name}</b><span className="lr-sub">{w.attackerName} · {w.defenderName}</span></span><Link to="/map?view=military" className="btn small quiet"><IconWar />{t('court.toWar')}</Link></li>
              ))}</ul></Panel>
            )}
          </Section>
          <Section title={t('letters.proposalsOut')} right={count(pendingOut, false)}>
            {outgoing.length === 0 ? <Panel><Empty>{t('letters.noProposalsOut')}</Empty></Panel> : (
              <Panel><div className="attn-list">{outgoing.map((p) => <ProposalRow key={p.id} p={p} day={day} house={h} cmd={cmd} />)}</div></Panel>
            )}
          </Section>
        </>
      )}

      {tab === 'intrigue' && <Intrigue h={h} cmd={cmd} />}
      {tab === 'factions' && <Factions h={h} world={world.data} cmd={cmd} />}
      </ScrollArea>

      <Sheet open={compose !== null} onClose={() => setCompose(null)} title={t('letters.compose.title')}>
        {compose && <ComposeForm house={h} initialTo={compose.to} onDone={() => setCompose(null)} />}
      </Sheet>
      <Sheet open={alliance} onClose={() => setAlliance(false)} title={t('letters.alliance.title')}>
        {alliance && <AllianceForm house={h} world={world.data} onDone={() => setAlliance(false)} />}
      </Sheet>
      <Sheet open={wardship} onClose={() => setWardship(false)} title={t('court.offerWardship')}>
        {wardship && <WardshipForm house={h} world={world.data} onDone={() => setWardship(false)} />}
      </Sheet>
    </Screen>
  );
}

function Intrigue({ h, cmd }: { h: HouseView; cmd: Cmd }) {
  const { t } = useTranslation();
  const unknown = t('scheme.unknownSoul');
  return (
    <>
      <Section title={t('court.yourSchemes')} icon={<IconDagger />}>
        <Panel>
          {h.schemes.length === 0 && <Empty>{t('scheme.empty')}</Empty>}
          {h.schemes.map((s: SchemeView) => (
            <div key={s.id} className="list-row" style={{ flexWrap: 'wrap' }}>
              <div className="grow">
                <b>{t('scheme.against', { name: s.targetName ?? unknown })}</b>
                <span className="lr-sub">{t(`scheme.type.${s.type}`)}{s.discovered && <> · <span className="neg">{t('scheme.discovered')}</span></>}</span>
              </div>
              <Btn small variant="danger" busy={cmd.pending} onClick={() => void cmd.run({ type: 'cancel_scheme', actorId: actorOf(h), schemeId: s.id })}>{t('scheme.abandon')}</Btn>
              <div style={{ flexBasis: '100%' }}><Bar label={t('scheme.progress')} value={Math.round(s.progress)} max={s.progressNeeded} /></div>
            </div>
          ))}
          <p className="small muted court-hint">{t('court.startHint')} <Link className="link" to="/search">{t('court.openSearch')}</Link></p>
          <Inline error={cmd.error} />
        </Panel>
      </Section>
      <Section title={t('scheme.plots')}>
        <Panel>
          {h.plotsAgainstUs.length === 0 ? <Empty>{t('court.noPlots')}</Empty> : h.plotsAgainstUs.map((s) => (
            <p key={s.id} className="small">
              <b>{t(`scheme.type.${s.type}`)}</b> — {s.leaderName
                ? t('scheme.plotAgainstLeader', { name: s.targetName ?? unknown, leader: s.leaderName })
                : t('scheme.plotAgainst', { name: s.targetName ?? unknown })}
            </p>
          ))}
        </Panel>
      </Section>
    </>
  );
}

// ------------------------------------------------------------------ factions

function Factions({ h, world, cmd }: { h: HouseView; world: WorldView; cmd: Cmd }) {
  const { t } = useTranslation();
  const titleOf = (id: Id) => world.titles.find((x) => x.id === id);
  return (
    <Section title={t('court.factions')}>
      <Panel>
        {h.factions.length === 0 && <Empty>{t('court.noFactions')}</Empty>}
        {h.factions.map((f: FactionView) => {
          const liege = titleOf(f.liegeTitleId);
          const leader = titleOf(f.leaderTitleId);
          const pct = Math.round(f.strength * 100);
          return (
            <div key={f.id} className="list-row" style={{ flexWrap: 'wrap' }}>
              <div className="grow">
                <b>{liege ? <TitleLink id={f.liegeTitleId} /> : f.liegeTitleId}</b>
                <div className="lr-sub">
                  <Badge tone="plain">{t(`faction.type.${f.type}`)}</Badge>
                  <span className="small muted">
                    {t('faction.leader', { name: leader?.holderName ?? t('faction.unknown') })}
                  </span>
                  <span className="small muted">{t('faction.members', { count: f.memberTitleIds.length })}</span>
                  <span className="small muted">{t('faction.formed', { date: formatDate(f.formedDay) })}</span>
                  {f.status !== 'growing' && <span className="small">{t(`faction.status.${f.status}`)}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '6px 0' }}>
                  <Bar label={t('faction.strength')} value={pct} max={100} />
                  <span className="small muted">{pct}%</span>
                  {f.ready && <Badge tone="gold">{t('faction.ready')}</Badge>}
                </div>
                <ul className="list">
                  {f.memberTitleIds.map((id) => {
                    const mt = titleOf(id);
                    return <li key={id} className="small">{mt?.holderName ?? mt?.name ?? id}</li>;
                  })}
                </ul>
              </div>
              {f.isMember && (
                <Btn small variant="danger" busy={cmd.pending}
                  onClick={() => void cmd.run({ type: 'leave_faction', actorId: actorOf(h) })}>
                  {t('faction.leave')}
                </Btn>
              )}
              {f.canJoin && (
                <Btn small variant="primary" busy={cmd.pending}
                  onClick={() => void cmd.run({ type: 'join_faction', actorId: actorOf(h), factionId: f.id })}>
                  {t('faction.join')}
                </Btn>
              )}
              {!f.isMember && !f.canJoin && (
                <span className="small muted">
                  {liege?.holderHouseId === h.house.id ? t('faction.whyNot.liege') : t('faction.whyNot.notVassal')}
                </span>
              )}
            </div>
          );
        })}
      </Panel>
    </Section>
  );
}

function ProposalRow({ p, incoming, day, house, cmd }: { p: Proposal; incoming?: boolean; day: number; house: HouseView; cmd: Cmd }) {
  const { t } = useTranslation();
  const eta = useEta();
  const [open, setOpen] = useState(false);
  const actor = actorOf(house);
  const pending = p.status === 'pending';
  const arrived = p.arriveDay <= day;
  const left = Math.max(0, p.expiresDay - day);
  const canAnswer = incoming && pending && arrived;
  // Round C2: a favour (hook) this house holds may be called in on this pending proposal.
  const callIn = !incoming && pending && !p.hookId
    ? house.hooks.find((k) => k.direction === 'held' && k.spent === null && k.targetId === p.toId)
    : undefined;
  const ftype = p.kind === 'faction_demand' && p.factionId
    ? house.factions.find((x) => x.id === p.factionId)?.type
    : undefined;
  return (
    <div className={`attn-item ${canAnswer ? 'has-btns' : ''}`}>
      <span className="attn-ico"><IconScroll /></span>
      <div className="attn-text">
        <b>{t(`proposal.kind.${p.kind}`)}</b>
        {p.hookId && <span className="tag">{t('letters.hookForced')}</span>}
        <span>
          {incoming ? <>{t('proposal.from')} <CharLink id={p.fromId} /></> : <>{t('proposal.to')} <CharLink id={p.toId} /></>}
          {' · '}{pending ? t('proposal.expires', { days: left }) : t(`proposal.status.${p.status}`)}
        </span>
        <button type="button" className="link small" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? t('ui.hideDetails') : t('ui.details')}</button>
        {open && (
          <span className="attn-detail">
            {p.kind === 'squire' && p.subjectAId && p.subjectBId && <><CharLink id={p.subjectAId} /> {t('proposal.toServe')} <CharLink id={p.subjectBId} /><br /></>}
            {p.kind !== 'squire' && p.subjectAId && p.subjectBId && <><CharLink id={p.subjectAId} /> &amp; <CharLink id={p.subjectBId} /><br /></>}
            {p.kind === 'service' && p.subjectAId && !p.subjectBId && <><CharLink id={p.subjectAId} /><br /></>}
            {p.kind === 'charter' && p.subjectAId && <>{t('proposal.charterFor')} <CharLink id={p.subjectAId} />{p.status === 'accepted' && <> · {t('proposal.charterSealed', { date: formatDate(p.expiresDay) })}</>}<br />{incoming && <>{t('proposal.charterTerms')}<br /></>}</>}
            {p.titleId && p.kind !== 'faction_demand' && <><TitleLink id={p.titleId} /><br /></>}
            {(p.kind === 'marriage' || p.kind === 'betrothal') && <>{t('proposal.dowry', { gold: p.dowry })} · {p.matrilineal ? t('proposal.matrilineal') : t('proposal.patrilineal')}<br /></>}
            {(p.kind === 'alliance' || p.kind === 'non_aggression') && p.durationYears !== undefined && <>{t('proposal.allianceDuration', { years: p.durationYears })}<br /></>}
            {p.kind === 'peace' && p.terms && <>{t(`proposal.terms.${p.terms}`)}<br /></>}
            {p.kind === 'wardship' && p.childId && <>{incoming ? <><CharLink id={p.fromId} /> {t('letters.wardship')}</> : t('letters.wardshipOut')} <CharLink id={p.childId} /><br /></>}
            {p.kind === 'faction_demand' && (
              <span>
                {ftype === 'independence' ? <>{t('proposal.demand.independence')} <TitleLink id={p.titleId} /></>
                  : ftype === 'claimant' ? <>{t('proposal.demand.claimant')} <TitleLink id={p.titleId} /> {t('proposal.demand.claimantFor')} {p.subjectAId && <CharLink id={p.subjectAId} />}</>
                  : ftype === 'lower_taxes' ? <>{t('proposal.demand.lower_taxes')} <TitleLink id={p.titleId} /> {t('proposal.demand.lowerTaxesTail', { tax: FACTION_DEMAND_TAX })}</>
                  : t('proposal.demand.unknown')}
                <br />
              </span>
            )}
            {t('letters.sentOn', { date: formatDate(p.sentDay) })} · {arrived ? t('letters.arrivedOn', { date: formatDate(p.arriveDay) }) : t('letters.arrivesOn', { date: formatDate(p.arriveDay), eta: eta(p.arriveDay) })}
          </span>
        )}
        {incoming && pending && !arrived && <span>{t('letters.notArrived', { eta: eta(p.arriveDay) })}</span>}
        {!incoming && pending && <span>{arrived ? t('letters.awaitingReply') : t('letters.onTheRoad')}</span>}
        {canAnswer && p.kind === 'faction_demand' && <span className="small muted">{t('proposal.demand.declineHint')}</span>}
      </div>
      {canAnswer && (
        <div className="attn-btns">
          <Btn small variant="primary" icon={<IconCheck />} busy={cmd.pending} onClick={() => void cmd.run({ type: 'answer_proposal', actorId: actor, proposalId: p.id, accept: true })}>{t('proposal.accept')}</Btn>
          {!p.hookId && <Btn small variant="danger" icon={<IconX />} busy={cmd.pending} onClick={() => void cmd.run({ type: 'answer_proposal', actorId: actor, proposalId: p.id, accept: false })}>{t('proposal.decline')}</Btn>}
        </div>
      )}
      {callIn && (
        <div className="attn-btns">
          <Btn small variant="primary" icon={<IconCheck />} busy={cmd.pending} onClick={() => void cmd.run({ type: 'use_hook', actorId: actor, hookId: callIn.id, proposalId: p.id })}>{t('letters.callHook')}</Btn>
        </div>
      )}
    </div>
  );
}

function LetterCard({ l, inbox, day, onReply }: { l: Letter; inbox: boolean; day: number; onReply: () => void }) {
  const { t } = useTranslation();
  const eta = useEta();
  const arrived = l.arriveDay <= day;
  const fresh = inbox && !l.read && arrived;
  return (
    <article className={`letter-item ${fresh ? 'unread' : ''}`}>
      <div className="row between">
        <div className="letter-from">{inbox ? <>{t('proposal.from')} <b><CharLink id={l.fromId} /></b></> : <>{t('proposal.to')} <b><CharLink id={l.toId} /></b></>}</div>
        {fresh && <Badge tone="gold">{t('letters.new')}</Badge>}
      </div>
      {arrived || !inbox ? <p className="letter-text">{l.text}</p> : <p className="empty">{t('letters.notArrived', { eta: eta(l.arriveDay) })}</p>}
      <p className="small muted" style={{ marginBottom: 0 }}>
        {t('letters.sentOn', { date: formatDate(l.sentDay) })} · {arrived ? t('letters.arrivedOn', { date: formatDate(l.arriveDay) }) : t('letters.arrivesOn', { date: formatDate(l.arriveDay), eta: eta(l.arriveDay) })}
      </p>
      {inbox && arrived && <div style={{ marginTop: 10 }}><Btn small variant="quiet" icon={<IconQuill />} onClick={onReply}>{t('letters.reply')}</Btn></div>}
    </article>
  );
}

function ComposeForm({ house, initialTo, onDone }: { house: HouseView; initialTo: Id | null; onDone: () => void }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [to, setTo] = useState<Id | null>(initialTo);
  const [text, setText] = useState('');
  const body = text.trim();
  return (
    <div>
      <p className="muted">{t('letters.compose.help')}</p>
      {to ? (
        <div className="kv"><span>{t('proposal.to')}</span><span><CharLink id={to} /> · <button type="button" className="link" onClick={() => setTo(null)}>{t('picker.change')}</button></span></div>
      ) : (
        <CharacterPicker value={to} onChange={(id) => setTo(id)} excludeHouseId={house.house.id} label={t('letters.compose.to')} />
      )}
      <div style={{ height: 12 }} />
      <Field label={t('letters.compose.text')} hint={t('letters.compose.count', { n: text.length, max: MAX_LEN })}>
        <textarea className="input" maxLength={MAX_LEN} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('letters.compose.placeholder')} />
      </Field>
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!to || body.length === 0} busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'send_letter', actorId: actorOf(house), toId: to!, text: body }); if (r.ok) onDone(); }}>
          {t('letters.compose.send')}
        </Btn>
      </div>
    </div>
  );
}

function AllianceForm({ house, world, onDone }: { house: HouseView; world: WorldView; onDone: () => void }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [target, setTarget] = useState<Id | null>(null);
  const [years, setYears] = useState<AllianceDurationYears>(10);
  const [yearsStr, setYearsStr] = useState<'5' | '10' | '20'>('10');
  const [pactType, setPactType] = useState<'alliance' | 'non_aggression'>('alliance');
  const allied = new Set(house.allyHouseIds);
  const pacts = new Set(house.napHouseIds);
  const bound = (id: Id) => (pactType === 'alliance' ? allied.has(id) : pacts.has(id));
  const others = world.houses
    .filter((x) => x.id !== house.house.id && !x.extinct)
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div>
      <p className="muted">{t(pactType === 'alliance' ? 'letters.alliance.help' : 'letters.nap.help')}</p>
      <Field label={t('letters.alliance.type')}>
        <Segmented<'alliance' | 'non_aggression'> value={pactType} onChange={setPactType}
          options={[{ value: 'alliance' as const, label: t('letters.alliance.typeAlliance') }, { value: 'non_aggression' as const, label: t('letters.alliance.typeNap') }]} />
      </Field>
      <Field label={t('letters.alliance.target')}>
        <select className="input" aria-label={t('letters.alliance.target')} value={target ?? ''} onChange={(e) => setTarget(e.target.value || null)}>
          <option value="">{t('letters.alliance.choose')}</option>
          {others.map((x) => (
            <option key={x.id} value={x.id} disabled={bound(x.id)}>
              {t(x.memberCount === 1 ? 'letters.alliance.house_one' : 'letters.alliance.house_other', { name: x.name, count: x.memberCount })}
              {bound(x.id) ? ` · ${t(pactType === 'alliance' ? 'letters.alliance.allied' : 'letters.alliance.pacted')}` : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('letters.alliance.duration')}>
        <Segmented<'5' | '10' | '20'> value={yearsStr} onChange={(v) => { setYearsStr(v); setYears(Number(v) as AllianceDurationYears); }} options={(['5', '10', '20'] as const).map((y) => ({ value: y, label: t('letters.alliance.years', { count: Number(y) }) }))} />
      </Field>
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!target} busy={cmd.pending}
          onClick={async () => {
            const r = pactType === 'alliance'
              ? await cmd.run({ type: 'propose_alliance', actorId: actorOf(house), targetHouseId: target!, durationYears: years })
              : await cmd.run({ type: 'propose_non_aggression', actorId: actorOf(house), targetHouseId: target!, durationYears: years });
            if (r.ok) onDone();
          }}>
          {t(pactType === 'alliance' ? 'letters.alliance.send' : 'letters.nap.send')}
        </Btn>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ wardship (Round C3)

function WardshipForm({ house, world, onDone }: { house: HouseView; world: WorldView; onDone: () => void }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [child, setChild] = useState<Id | null>(null);
  const [target, setTarget] = useState<Id | null>(null);
  const memberList = Array.from(memberIds(house));
  const kids = useCharacters(memberList).map((q) => q.data).filter((x): x is NonNullable<typeof x> => !!x);
  const onTheWay = new Set(house.outgoingProposals.filter((p) => p.status === 'pending' && p.kind === 'wardship').map((p) => p.childId));
  // Eligible wards: our own living children of school age (6–15) with no wardship proposal already sent.
  const eligible = kids
    .filter((c) => c.alive && c.age >= EDUCATION_START_AGE && c.age < AGE_OF_MAJORITY && !onTheWay.has(c.id))
    .sort((a, b) => b.age - a.age);
  const others = world.houses
    .filter((x) => x.id !== house.house.id && !x.extinct)
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div>
      <p className="muted">{t('court.wardship.help')}</p>
      <p className="muted small">{t('court.wardship.squireHint')}</p>
      <Field label={t('court.wardship.child')}>
        <select className="input" aria-label={t('court.wardship.child')} value={child ?? ''} onChange={(e) => setChild(e.target.value || null)}>
          <option value="">{t('court.wardship.chooseChild')}</option>
          {eligible.map((c) => (
            <option key={c.id} value={c.id}>{c.firstName} ({c.age})</option>
          ))}
        </select>
      </Field>
      <Field label={t('letters.alliance.target')}>
        <select className="input" aria-label={t('letters.alliance.target')} value={target ?? ''} onChange={(e) => setTarget(e.target.value || null)}>
          <option value="">{t('letters.alliance.choose')}</option>
          {others.map((x) => (
            <option key={x.id} value={x.id}>
              {t(x.memberCount === 1 ? 'letters.alliance.house_one' : 'letters.alliance.house_other', { name: x.name, count: x.memberCount })}
            </option>
          ))}
        </select>
      </Field>
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!child || !target} busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'propose_wardship', actorId: actorOf(house), childId: child!, toHouseId: target! }); if (r.ok) onDone(); }}>
          {t('court.wardship.send')}
        </Btn>
      </div>
    </div>
  );
}
